import React, { useEffect, useState } from "react";
import { Send, CheckCircle2, Mail, Phone, MapPin, ArrowLeft } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";

const ContactPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const artistId = params.get("artistId") || "";

  const [artists, setArtists] = useState([]);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    company: "",
    artistId: artistId,
    artistName: "",
    eventType: "",
    eventDate: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  }, []);

  // Sync artistId -> artistName
  useEffect(() => {
    if (form.artistId && artists.length) {
      const a = artists.find((x) => x.id === form.artistId);
      if (a) setForm((f) => ({ ...f, artistName: a.name }));
    }
  }, [form.artistId, artists]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.artistId) delete payload.artistId;
      await api.post("/bookings", payload);
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send request");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#121212] pt-32 pb-20" data-testid="contact-success">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-full border border-gold/50 flex items-center justify-center mx-auto mb-8 pulse-gold">
            <CheckCircle2 size={32} className="text-gold" />
          </div>
          <h1 className="font-display font-light text-5xl text-white mb-6">
            Request <span className="gold-gradient-text italic">Received</span>
          </h1>
          <p className="font-body text-white/50 text-sm leading-relaxed mb-10 max-w-md mx-auto">
            Thank you, {form.name}. Our team has received your booking request and will reach out within 48 hours to discuss your project.
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate("/")} className="btn-outline-gold px-6 py-3 text-xs rounded-sm">Back to Home</button>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm({ ...form, message: "", eventType: "", eventDate: "" });
              }}
              className="btn-gold px-6 py-3 text-xs rounded-sm"
            >
              Send Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] pt-24 pb-20" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/40 hover:text-gold transition-colors text-xs font-body tracking-wider uppercase mb-10"
        >
          <ArrowLeft size={13} />
          <span>Back</span>
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 bg-gold/60" />
          <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Get In Touch</span>
        </div>
        <h1 className="font-display font-light text-5xl md:text-6xl text-white mb-4">
          Contact <span className="gold-gradient-text italic">Us</span>
        </h1>
        <p className="font-body text-white/50 text-sm max-w-2xl mb-14">
          Every great campaign starts with a conversation. Share your vision with us and we'll curate the perfect artist experience for your brand or production.
        </p>

        <div className="grid lg:grid-cols-3 gap-10">
          {/* Left: Info */}
          <div className="space-y-8 lg:col-span-1">
            <div className="glass rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <Mail size={14} className="text-gold" />
                <span className="font-body text-gold text-[0.6rem] tracking-[0.3em] uppercase font-semibold">Email</span>
              </div>
              <a href="mailto:hello@mrva.com" className="font-body text-white text-sm hover:text-gold transition-colors">hello@mrva.com</a>
            </div>
            <div className="glass rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <Phone size={14} className="text-gold" />
                <span className="font-body text-gold text-[0.6rem] tracking-[0.3em] uppercase font-semibold">Phone</span>
              </div>
              <a href="tel:+442071234567" className="font-body text-white text-sm hover:text-gold transition-colors">+44 20 7123 4567</a>
            </div>
            <div className="glass rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin size={14} className="text-gold" />
                <span className="font-body text-gold text-[0.6rem] tracking-[0.3em] uppercase font-semibold">Studio</span>
              </div>
              <p className="font-body text-white text-sm leading-relaxed">
                MRVA House<br />
                12 Shoreditch High Street<br />
                London, EC2A 3JL<br />
                United Kingdom
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 glass rounded-sm p-8 md:p-10 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Full Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                  data-testid="contact-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Phone</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                />
              </div>
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Company</label>
                <input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Artist of Interest</label>
                <select
                  value={form.artistId}
                  onChange={(e) => setForm({ ...form, artistId: e.target.value })}
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                  data-testid="contact-artist"
                >
                  <option value="" style={{ background: "#121212" }}>Any / Not Sure</option>
                  {artists.map((a) => (
                    <option key={a.id} value={a.id} style={{ background: "#121212" }}>
                      {a.name} — {a.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Event Type</label>
                <input
                  value={form.eventType}
                  onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                  placeholder="e.g. Music video, Brand campaign, Live show"
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                />
              </div>
            </div>

            <div>
              <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Event Date (approximate)</label>
              <input
                type="date"
                value={form.eventDate}
                onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
                className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                style={{ colorScheme: "dark" }}
              />
            </div>

            <div>
              <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Your Message *</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={5}
                placeholder="Tell us about your project, timeline, budget range, and any specific requirements."
                className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body resize-none"
                data-testid="contact-message"
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3">
                <p className="font-body text-red-400 text-xs">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-gold px-10 py-4 text-xs rounded-sm flex items-center gap-3"
              data-testid="contact-submit"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send size={13} />
                  <span>Send Request</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
